// noinspection JSCheckFunctionSignatures

import PropTypes from 'prop-types'
import { createContext, useEffect, useState, useReducer, useMemo, useContext, useCallback } from 'react'
import { useLocalStorage } from 'react-use'
import { useTimer } from 'use-timer'

// import filterByReference from '@/utils/data/filterByReference'
import { convertLineUpForMutation, convertLogsForMutation } from '@/utils/data/matchMode'
import { useRouter } from 'next/router'
import filterByReference from '@/utils/data/filterByReference'

const initialMatchState = {
  id: null,
  uid: null,
  now: Date.now(),
  visible: false,
  start: false,
  startTime: null,
  endTime: null,
  paused: false,
  halfTime: false,
  secondHalf: false,
  pauseOverlay: false,
  warning: false,
  score: {
    team: 0,
    opponent: 0
  },
  initialLineUp: [],
  currentLineUp: [],
  substitutes: [], // players that are not in the current line up but are included initial line up
  absents: [], // players from the team that are not in initial line up
  logs: []
}

export const MatchContext = createContext(initialMatchState)

// Reducer functions that trigger state actions
// See https://reactjs.org/docs/hooks-reference.html#usereducer
const matchReducer = (state, action) => {
  switch (action.type) {
    case 'MATCH_LOG_OPEN': {
      return { ...state, visible: true }
    }
    case 'MATCH_LOG_CLOSE': {
      return { ...state, visible: false }
    }
    case 'MATCH_START': {
      return { ...state, start: action.start, startTime: action.startTime }
    }
    case 'MATCH_END': {
      return { ...state }
    }
    case 'MATCH_ENDTIME': {
      return { ...state, endTime: action.endTime }
    }
    case 'MATCH_PAUSED': {
      return { ...state, paused: action.paused }
    }
    case 'MATCH_PAUSE_OVERLAY': {
      return { ...state, pauseOverlay: action.pauseOverlay }
    }
    case 'MATCH_HALFTIME': {
      return { ...state, halfTime: action.halfTime }
    }
    case 'MATCH_WARNING': {
      return { ...state, warning: action.warning }
    }
    case 'MATCH_SECONDHALF': {
      return { ...state, secondHalf: action.secondHalf }
    }
    case 'MATCH_UPDATE_SCORE': {
      return { ...state, score: action.score }
    }
    case 'MATCH_UPDATE_ABSENTS': {
      return { ...state, absents: action.absents }
    }
    case 'MATCH_UPDATE_SUBSTITUTES': {
      return { ...state, substitutes: action.substitutes }
    }
    case 'MATCH_UPDATE_INITIAL_LINEUP': {
      return { ...state, initialLineUp: action.initialLineUp }
    }
    case 'MATCH_UPDATE_LINEUP': {
      return { ...state, currentLineUp: action.currentLineUp }
    }
    case 'MATCH_UPDATE_LOGS': {
      return { ...state, logs: action.logs }
    }
    case 'MATCH_UPDATE_TIME': {
      return { ...state, time: action.time }
    }
    case 'MATCH_INITIAL_LOAD': {
      return { ...state, ...action.data }
    }
    case 'MATCH_UPDATE_MATCH': {
      return { ...state, ...action.data }
    }
    case 'MATCH_SAVE_ENTRY': {
      return { ...state }
    }
    case 'MATCH_RESET': {
      return {
        ...state,
        ...initialMatchState
      }
    }
  }
}

const MatchProvider = (props) => {
  const { children, match, team, enabled } = props

  // Match state using reducer
  const [state, dispatch] = useReducer(matchReducer, initialMatchState)
  const [matchData, setMatchData] = useState(match)

  // Timer state
  // See https://github.com/thibaultboursier/use-timer
  const { time, start, pause, reset, status, advanceTime } = useTimer({ autostart: false, initialTime: 0 })

  // Match log general states
  const [selectedPlayer, setSelectedPlayer] = useState(null)
  const [playerFormationRef, setPlayerFormationRef] = useState(null)

  // Match log navigation related states
  const [tabNav, setTabNav] = useState('formation')
  const [sidebarTabNav, setSidebarTabNav] = useState('substitute')
  const [showSidebar, setShowSidebar] = useState(false)
  const [showEvaluation, setShowEvaluation] = useState(false)

  const [matchLogState, setMatchLogState] = useLocalStorage(match.uid || 'matchLog')
  const router = useRouter()

  // Initial app load
  useEffect(() => {
    if (localStorage.getItem('matchLog')) {
      localStorage.removeItem('matchLog')
    }
    if (matchData?.matchStatus === 'ended') {
      dispatch({ type: 'MATCH_RESET' })
    }
    if (matchLogState) {
      if (status !== 'RUNNING' && matchLogState?.timer?.status === 'RUNNING') {
        start()
      }
      if (status === 'STOPPED' && matchLogState?.timer?.status === 'PAUSED') {
        start()
        pause()
      }

      if (time === 0 && matchLogState?.timer?.time) {
        advanceTime(matchLogState?.timer?.time)
      }
    }

    return () => {}
  }, [matchData.uid, matchLogState])

  // useEffect(() => {
  //   if (matchLogState.uid !== match.uid) {
  //     if (JSON.stringify(state.absents) !== JSON.stringify(matchLogState.absents)) {
  //       dispatch({ type: 'MATCH_UPDATE_ABSENTS', absents: matchLogState.absents || [] })
  //     }
  //     if (JSON.stringify(state.substitutes) !== JSON.stringify(matchLogState.substitutes)) {
  //       dispatch({ type: 'MATCH_UPDATE_SUBSTITUTES', substitutes: matchLogState.substitutes || [] })
  //     }
  //   }
  // }, [matchData, team])

  useEffect(() => {
    document.body.style.overflow = state?.visible ? 'hidden' : ''
  }, [state?.visible])

  useEffect(() => {
    if (match?.matchStatus === 'ended') {
      localStorage.removeItem(match?.uid)
    } else {
      saveLocalMatchLogState()
    }
  }, [time, status])

  useEffect(() => {
    if (router.route === '/match/id/[id]' && matchData.matchStatus === 'ongoing') {
      if (JSON.stringify(matchData?.matchScore[0]) !== JSON.stringify(state.score)) {
        saveLocalMatchLogState()
      }
    }
  }, [state.score, matchData])

  // Advance time outside of the context
  const setMatchTime = (seconds) => {
    if (!seconds) return
    advanceTime(seconds)
  }

  const saveMatch = useCallback(
    async (state) => {
      if (!state.match.id) {
        console.error('Match without id')
        return
      }
      let fieldsToUpdate = {}

      // If the match has ended, only allow matchlogs to be updated
      if (state.match.matchStatus === 'ended') {
        fieldsToUpdate = {
          matchLogs: convertLogsForMutation(state.logs)
        }
      } else {
        fieldsToUpdate = {
          matchDay: state.match.matchDay,
          score: [{ col1: state.score?.team, col2: state.score?.opponent }],
          matchStatus: state.endTime ? 'ended' : 'ongoing',
          matchStartedTime: state.startTime,
          matchEndedTime: state.endTime,
          initialLineUp: convertLineUpForMutation(state.initialLineUp, state.visible),
          matchLogs: convertLogsForMutation(state.logs)
        }
      }
      const response = await fetch('/api/match-mode/save-match', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: state.match.id,
          ...fieldsToUpdate
        })
      })

      const newState = await response.json()

      if (newState.match) {
        setMatchData(newState.match)

        if (newState.match.matchStatus === 'ended') {
          localStorage.removeItem(newState.match.uid)
        }
      }

      dispatch({ type: 'MATCH_SAVE_ENTRY', data: newState })
    },
    [dispatch]
  )

  const refreshMatch = useCallback(
    async (state) => {
      // fetch data from match
      const response = await fetch('/api/match-mode/get-match', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: state.match.id
        })
      })
      const newState = await response.json()
      if (newState.entry) {
        const substitutes = filterByReference(newState.entry.initialLineUp, newState.entry.currentLineUp) || []
        const absents = filterByReference(newState.entry.team[0].players, newState.entry.initialLineUp) || []
        setMatchData({ ...newState.entry, substitutes, absents })
      }
    },
    [dispatch]
  )

  const saveLocalMatchLogState = () => {
    if (!state.start) return

    const localStorageState = {
      ...state,
      timer: {
        status,
        time
      }
    }
    setMatchLogState(localStorageState)
  }

  // Memoize the value of match state
  const matchLogValue = useMemo(
    () => ({
      ...state,
      saveMatch,
      saveLocalMatchLogState,
      setMatchTime,
      refreshMatch
    }),
    [state]
  )

  // TODO maybe not pass any state values for non match mode users
  const matchContextStateValues = {
    ...matchLogValue,
    dispatch,
    enabled,
    team,
    match: matchData,
    setMatchData,
    timer: {
      time,
      start,
      pause,
      reset,
      status,
      advanceTime
    },

    // TODO move this to component or to team page state
    // graphViewValue,
    // setGraphViewValue,

    tabNav,
    setTabNav,
    showSidebar,
    setShowSidebar,
    sidebarTabNav,
    setSidebarTabNav,
    showEvaluation,
    setShowEvaluation,

    // TODO to discuss
    selectedPlayer,
    setSelectedPlayer,
    playerFormationRef,
    setPlayerFormationRef
  }

  return <MatchContext.Provider value={matchContextStateValues}>{children}</MatchContext.Provider>
}

MatchProvider.defaultProps = {
  match: {},
  team: {},
  enabled: false
}

MatchProvider.propTypes = {
  match: PropTypes.object,
  team: PropTypes.object,
  enabled: PropTypes.bool,
  children: PropTypes.object
}

export default MatchProvider

// Method to export match state values easier
export const useMatchLog = () => {
  const context = useContext(MatchContext)
  if (context === undefined) {
    throw new Error(`useMatch must be used within a MatchProvider`)
  }
  return context
}
